/**
 * AssetShadowService.js — API client for the Asset-Shadows (Digital Twin) API.
 *
 * Asset-shadows represent real-time state snapshots of physical assets (vehicles,
 * cameras, meters, control units). Each shadow holds the latest location,
 * speed, ignition, fuel, online status — updated continuously by the
 * telemetry pipeline.
 *
 * Used by DeviceStore.fetchPositions() to hydrate the fleet map with
 * last-known positions on initial load, before Socket.IO telemetry
 * takes over for real-time updates.
 *
 * Endpoints (see docs/openapi.yaml):
 *   GET    /asset-shadows              — list all asset shadows (with online filter)
 *   GET    /asset-shadows/stats        — shadow statistics (total, online, offline)
 *   GET    /asset-shadows/location     — geospatial query (within radius)
 *   GET    /asset-shadows/:id          — single shadow
 *   PUT    /asset-shadows/:id          — update shadow state (simulate telemetry)
 */

import { createApiClient } from './ApiClientFactory'

const BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:5000'
const apiClient = createApiClient(BASE_URL)

export default {
  /**
   * List asset shadows.
   *
   * Query params (all optional):
   *   GET /api/v1/asset-shadows?project_id=proj_X          → shadows for project
   *   GET /api/v1/asset-shadows                            → all org shadows
   *   GET /api/v1/asset-shadows?project_id=proj_X&online=true → online only
   *
   * @param {Object} params
   * @param {String}  [params.project_id] — scope to a specific project
   * @param {Boolean} [params.online]     — filter by online status
   * @param {Number}  [params.limit]      — max results (default server-side)
   * @param {Number}  [params.skip]       — pagination offset
   * @returns {Promise<Array>} Array of asset-shadow documents
   */
  async getShadows(params = {}) {
    const response = await apiClient.get('/asset-shadows', { params })
    return response.data
  },

  /**
   * Get asset shadow statistics (total, online, offline counts).
   */
  async getShadowStats() {
    const response = await apiClient.get('/asset-shadows/stats')
    return response.data
  },

  /**
   * Geospatial query: get shadows within a radius of a coordinate.
   * @param {Number} longitude — center longitude
   * @param {Number} latitude  — center latitude
   * @param {Number} maxDistance — radius in meters (default 5000)
   */
  async getShadowsByLocation(longitude, latitude, maxDistance = 5000) {
    const response = await apiClient.get('/asset-shadows/location', {
      params: { longitude, latitude, maxDistance },
    })
    return response.data
  },

  /**
   * Get a single asset shadow by ID.
   * @param {String} shadowId
   */
  async getShadow(shadowId) {
    const response = await apiClient.get(`/asset-shadows/${shadowId}`)
    return response.data
  },

  /**
   * Update asset shadow state (simulates a telemetry event).
   * @param {String} shadowId
   * @param {Object} payload — { online, state: { measurement, event, ... }, position, geometry, asset_type, domain }
   */
  async updateShadow(shadowId, payload) {
    const response = await apiClient.put(`/asset-shadows/${shadowId}`, payload)
    return response.data
  },
}
